import React, { useState, useEffect, useRef } from 'react';
import { Status, Priority } from '../../types';
import { ISSUE_CATEGORIES } from '../../constants';

/**
 * Props for the FilterControls component.
 */
interface FilterControlsProps {
  status: Status[];
  onStatusChange: (status: Status[]) => void;
  priority: Priority[];
  onPriorityChange: (priority: Priority[]) => void;
  category: string[];
  onCategoryChange: (categories: string[]) => void;
}

/**
 * A dropdown component that allows users to filter maintenance tasks by status, priority, and category.
 * @param {FilterControlsProps} props - The props for the component.
 * @returns {React.ReactElement} - The rendered filter controls.
 */
const FilterControls: React.FC<FilterControlsProps> = ({ status, onStatusChange, priority, onPriorityChange, category, onCategoryChange }) => {
    // State to manage the visibility of the filter dropdown.
    const [isOpen, setIsOpen] = useState(false);
    // Temporary state to hold filter changes before they are applied. This allows users to cancel their selections.
    const [tempStatus, setTempStatus] = useState<Status[]>(status);
    const [tempPriority, setTempPriority] = useState<Priority[]>(priority);
    const [tempCategory, setTempCategory] = useState<string[]>(category);
    const wrapperRef = useRef<HTMLDivElement>(null);
    
    const statusOptions: Status[] = [Status.Open, Status.InProgress, Status.Completed];
    const priorityOptions: Priority[] = Object.values(Priority);

    // Sync temporary state with the actual props whenever the dropdown is opened.
    useEffect(() => {
        if (isOpen) {
            setTempStatus(status);
            setTempPriority(priority);
            setTempCategory(category);
        }
    }, [status, priority, category, isOpen]);

    // Effect to handle clicking outside the component to close the dropdown.
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    /**
     * Toggles a status option in the temporary filter state.
     * @param {Status} s - The status to toggle.
     */
    const handleStatusToggle = (s: Status) => {
        setTempStatus(prev => 
            prev.includes(s)
                ? prev.filter(item => item !== s)
                : [...prev, s]
        );
    };

    /**
     * Toggles a priority option in the temporary filter state.
     * @param {Priority} p - The priority to toggle.
     */
    const handlePriorityToggle = (p: Priority) => {
        setTempPriority(prev =>
            prev.includes(p)
                ? prev.filter(item => item !== p)
                : [...prev, p]
        );
    };

    /**
     * Toggles a category option in the temporary filter state.
     * @param {string} c - The category key to toggle.
     */
    const handleCategoryToggle = (c: string) => {
        setTempCategory(prev =>
            prev.includes(c)
                ? prev.filter(item => item !== c)
                : [...prev, c]
        );
    };


    /**
     * Applies the selected temporary filters to the parent component's state.
     */
    const handleApply = () => {
        onStatusChange(tempStatus);
        onPriorityChange(tempPriority);
        onCategoryChange(tempCategory);
        setIsOpen(false);
    };

    /**
     * Clears all temporary and active filters.
     */
    const handleClear = () => {
        setTempStatus([]);
        setTempPriority([]);
        setTempCategory([]);
        onStatusChange([]);
        onPriorityChange([]);
        onCategoryChange([]);
        setIsOpen(false);
    };

    // Calculate the total number of active filters to display in the badge.
    const activeFilterCount = status.length + priority.length + category.length;

    /**
     * Gets the appropriate CSS classes for a filter chip based on whether it's selected.
     * @param {boolean} isSelected - Whether the filter chip is currently selected.
     * @returns {string} - The Tailwind CSS classes.
     */
    const getChipClasses = (isSelected: boolean) => {
        return `px-3 py-1.5 rounded-full cursor-pointer border font-medium text-sm transition-all duration-200 ease-in-out transform hover:scale-105 ${
            isSelected 
                ? 'bg-indigo-600 text-white border-indigo-600' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
        }`;
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`inline-flex items-center justify-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors ${
                    activeFilterCount > 0 
                        ? 'bg-indigo-100 text-indigo-700 border-indigo-300' 
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                aria-haspopup="true"
                aria-expanded={isOpen}
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                </svg>
                Filter
                {activeFilterCount > 0 && (
                    <span className="ml-2 bg-indigo-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {activeFilterCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute top-full mt-2 w-72 sm:w-96 left-0 md:w-[600px] md:right-0 md:left-auto bg-white rounded-lg shadow-2xl border border-gray-200 z-20 origin-top-left md:origin-top-right animate-fade-in-down">
                    <div className="p-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-800">Filter Tasks</h3>
                    </div>

                    <div className="p-4 max-h-[60vh] overflow-y-auto md:grid md:grid-cols-2 md:gap-x-6">
                        <div className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                <div className="flex flex-wrap gap-2">
                                    {statusOptions.map(s => (
                                        <button key={s} onClick={() => handleStatusToggle(s)} className={getChipClasses(tempStatus.includes(s))}>
                                            {s.charAt(0).toUpperCase() + s.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                                <div className="flex flex-wrap gap-2">
                                    {priorityOptions.map(p => (
                                        <button key={p} onClick={() => handlePriorityToggle(p)} className={getChipClasses(tempPriority.includes(p))}>
                                            {p.charAt(0).toUpperCase() + p.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 md:mt-0">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(ISSUE_CATEGORIES).map(([key, value]) => (
                                    <button key={key} onClick={() => handleCategoryToggle(key)} className={getChipClasses(tempCategory.includes(key))}>
                                        {value}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-50 px-4 py-3 flex justify-between items-center rounded-b-lg">
                        <button
                            onClick={handleClear}
                            className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-md transition-colors"
                        >
                            Clear All
                        </button>
                        <button
                            onClick={handleApply}
                            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilterControls;
